import { Dispatch, SetStateAction } from 'react'
import * as yup from 'yup'

import type { TInfiniteTablePaginationArgs } from '~/components/table/infiniteTable'

import { EColumnSort } from '~/components/table/models'
import { TBrandPayload } from '~/features/brand/models'
import {
  ECampaignChannel,
  ECampaignExecutionMode,
  ECampaignType,
  ETemplateVersionStatus,
  TCampaignFact,
  TMergeField,
} from '~/features/campaigns/models'
import { EOrgBillingMode } from '~/features/organizations/models'
import { TUser } from '~/features/user/models'
import {
  EVoiceTemplateDisplays,
  EVoiceTemplateGender,
  EVoiceTemplateKeys,
} from '~/features/voiceTemplates/models'
import { ECognitoGroups } from '~/models/auth'

export enum EUpdateTierSource {
  stripeWebhook = 'STRIPE_WEBHOOK',
  adminUI = 'ADMIN_UI',
  scheduledJob = 'SCHEDULED_JOB',
}

export type TPromptMetadataFields = Pick<
  TPromptMetadata,
  | EPromptMetadataKeys.id
  | EPromptMetadataKeys.name
  | EPromptMetadataKeys.category
  | EPromptMetadataKeys.type
  | EPromptMetadataKeys.subType
  | EPromptMetadataKeys.description
  | EPromptMetadataKeys.active
  | EPromptMetadataKeys.prompt
>

export enum EPromptMetadataKeys {
  id = 'id',
  name = 'name',
  category = 'category',
  type = 'type',
  subType = 'subType',
  description = 'description',
  active = 'active',
  prompt = 'prompt',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
}

export type TPromptMetadataSortableField =
  | EPromptMetadataKeys.name
  | EPromptMetadataKeys.category
  | EPromptMetadataKeys.type
  | EPromptMetadataKeys.subType
  | EPromptMetadataKeys.description
  | EPromptMetadataKeys.active

export enum EPromptMetadataFiltersFormKeys {
  search = 'search',
  status = 'status',
  category = 'category',
  type = 'type',
  subType = 'subType',
}

export enum EPromptMetadataStatusFilter {
  all = 'all',
  active = 'active',
  inactive = 'inactive',
}

export type TPromptMetadataFiltersForm = {
  [EPromptMetadataFiltersFormKeys.search]: string
  [EPromptMetadataFiltersFormKeys.status]: EPromptMetadataStatusFilter
  [EPromptMetadataFiltersFormKeys.category]: string
  [EPromptMetadataFiltersFormKeys.type]: string
  [EPromptMetadataFiltersFormKeys.subType]: string
}

export type TPromptMetadataTableParamsContext = {
  pagination: TInfiniteTablePaginationArgs
  setPagination: Dispatch<SetStateAction<TInfiniteTablePaginationArgs>>
  sortBy: TPromptMetadataSortableField | undefined
  setSortBy: Dispatch<SetStateAction<TPromptMetadataSortableField | undefined>>
  sortDirection: EColumnSort | undefined
  setSortDirection: Dispatch<SetStateAction<EColumnSort | undefined>>
}

export type TPromptMetadata = {
  [EPromptMetadataKeys.id]: string
  [EPromptMetadataKeys.name]: string
  [EPromptMetadataKeys.category]: string
  [EPromptMetadataKeys.type]: string
  [EPromptMetadataKeys.subType]: string
  [EPromptMetadataKeys.description]: string
  [EPromptMetadataKeys.active]: boolean
  [EPromptMetadataKeys.prompt]: string
  [EPromptMetadataKeys.createdDate]: Date
  [EPromptMetadataKeys.updatedDate]: Date
}

export type TFetchedAdminUser = Omit<TUser, 'paymentTier'> & {
  Org: {
    paymentTier: ECognitoGroups
  }
}

export type TRecentLoginAsUserEntry = {
  id: string
  email: string
  cognitoUid?: string
  orgId?: string
  lastUsedAt: string
}

export type TRecentLoginAsUserFields = Omit<
  TRecentLoginAsUserEntry,
  'lastUsedAt'
>

export type TSearchForUsersTableRow = Omit<
  TRecentLoginAsUserEntry,
  'lastUsedAt'
> & {
  lastUsedAt?: string
}

export enum EGetCheckoutLinkKeys {
  product = 'product',
  amount = 'amount',
  recurrence = 'recurrence',
  promotionCode = 'promotionCode',
}

export enum EGetCheckoutLinkProduct {
  plus = ECognitoGroups.customerPlusPlan,
  basic = ECognitoGroups.customerBasicPlan,
}

export enum EGetCheckoutLinkRecurrence {
  month = 'month',
  year = 'year',
}

export type TGetCheckoutLinkForm = {
  [EGetCheckoutLinkKeys.product]: EGetCheckoutLinkProduct
  [EGetCheckoutLinkKeys.amount]: number
  [EGetCheckoutLinkKeys.recurrence]: EGetCheckoutLinkRecurrence
  [EGetCheckoutLinkKeys.promotionCode]: string
}

export type TGetCheckoutLinkProductIds = {
  [x: string]: {
    [x in EGetCheckoutLinkProduct]: string
  }
}

export enum EGetCheckoutLinkPayloadKeys {
  userId = 'userId',
  priceAmount = 'priceAmount',
  recurrence = 'recurrence',
  productId = 'productId',
  promotionCode = 'promotionCode',
  productPlan = 'productPlan',
}

export type TGetCheckoutLinkFormPayload = Omit<
  TGetCheckoutLinkPayload,
  EGetCheckoutLinkPayloadKeys.productId
> & {
  [EGetCheckoutLinkPayloadKeys.productId]?: string
}

export type TGetCheckoutLinkPayload = {
  [EGetCheckoutLinkPayloadKeys.userId]: string
  [EGetCheckoutLinkPayloadKeys.priceAmount]: number
  [EGetCheckoutLinkPayloadKeys.recurrence]: EGetCheckoutLinkRecurrence
  [EGetCheckoutLinkPayloadKeys.productId]: string
  [EGetCheckoutLinkPayloadKeys.promotionCode]: string
  [EGetCheckoutLinkPayloadKeys.productPlan]: EGetCheckoutLinkProduct
}

export type TGetCheckoutLinkErrorResponse = {
  statusCode: number
  body: string
}

export type TGetCheckoutLinkSuccessResponse = {
  url: string
}

export enum ECampaignTemplatesFiltersKeys {
  search = 'search',
  status = 'status',
}

export enum ECampaignTemplatesFiltersStatus {
  all = 'all',
  active = 'active',
  deprecated = 'deprecated',
  draft = 'draft',
}

export type TCampaignTemplatesFilters = {
  [ECampaignTemplatesFiltersKeys.search]: string
  [ECampaignTemplatesFiltersKeys.status]: string
}
export enum ECampaignTemplateFormKeys {
  friendlyName = 'friendlyName',
  displayName = 'displayName',
  description = 'description',
  ownerType = 'ownerType',
  visibility = 'visibility',
  campaignType = 'campaignType',
  editorVariant = 'editorVariant',
  executionMode = 'executionMode',
  channels = 'channels',
  firstOutreachTime = 'firstOutreachTime',
  maxReachouts = 'maxReachouts',
  cadence = 'cadence',
  cadenceCustom = 'cadenceCustom',
  rules = 'rules',
  outreachInstructionsText = 'outreachInstructionsText',
  qualificationCriteria = 'qualificationCriteria',
  campaignFacts = 'campaignFacts',
  verticalTags = 'verticalTags',
  searchKeywords = 'searchKeywords',
  mergeFields = 'mergeFields',
  introMessage = 'introMessage',
  voicemailMessage = 'voicemailMessage',
  firstTextMessage = 'firstTextMessage',
  firstEmailBody = 'firstEmailBody',
  firstEmailSubject = 'firstEmailSubject',
  campaignGoals = 'campaignGoals',
  firstOutreachChannel = 'firstOutreachChannel',
}

export type TCreateCampaignTemplateForm = {
  [ECampaignTemplateFormKeys.friendlyName]: string
  [ECampaignTemplateFormKeys.displayName]: string
  [ECampaignTemplateFormKeys.description]: string
  [ECampaignTemplateFormKeys.ownerType]: string
  [ECampaignTemplateFormKeys.visibility]: string
  [ECampaignTemplateFormKeys.campaignType]: string
  [ECampaignTemplateFormKeys.editorVariant]: string
  [ECampaignTemplateFormKeys.executionMode]: ECampaignExecutionMode | null
  [ECampaignTemplateFormKeys.channels]: ECampaignChannel[]
  [ECampaignTemplateFormKeys.firstOutreachTime]: string | undefined
  [ECampaignTemplateFormKeys.maxReachouts]: number
  [ECampaignTemplateFormKeys.cadence]: string
  [ECampaignTemplateFormKeys.cadenceCustom]: string
  [ECampaignTemplateFormKeys.outreachInstructionsText]: string
  [ECampaignTemplateFormKeys.qualificationCriteria]: string
  [ECampaignTemplateFormKeys.campaignFacts]: TCampaignFact[]
  [ECampaignTemplateFormKeys.verticalTags]: string[]
  [ECampaignTemplateFormKeys.searchKeywords]: string[]
  [ECampaignTemplateFormKeys.mergeFields]: TMergeField[]
  [ECampaignTemplateFormKeys.introMessage]: string
  [ECampaignTemplateFormKeys.voicemailMessage]: string
  [ECampaignTemplateFormKeys.firstTextMessage]: string
  [ECampaignTemplateFormKeys.firstEmailBody]: string
  [ECampaignTemplateFormKeys.firstEmailSubject]: string
  [ECampaignTemplateFormKeys.campaignGoals]: string
  [ECampaignTemplateFormKeys.firstOutreachChannel]: string | null
}

export const createCampaignTemplateSchema = yup.object({
  [ECampaignTemplateFormKeys.friendlyName]: yup
    .string()
    .min(3, 'Friendly name must be at least 3 characters')
    .max(255, 'Friendly name must be at most 255 characters')
    .required('Friendly name is required'),
  [ECampaignTemplateFormKeys.displayName]: yup
    .string()
    .min(3, 'Display name must be at least 3 characters')
    .max(255, 'Display name must be at most 255 characters')
    .required('Display name is required'),
  [ECampaignTemplateFormKeys.description]: yup
    .string()
    .min(3, 'Description must be at least 3 characters')
    .max(2000, 'Description must be at most 2000 characters'),
  [ECampaignTemplateFormKeys.ownerType]: yup
    .string()
    .required('Owner type is required'),
  [ECampaignTemplateFormKeys.visibility]: yup
    .string()
    .required('Visibility is required'),
  [ECampaignTemplateFormKeys.campaignType]: yup
    .string()
    .required('Campaign type is required'),
  [ECampaignTemplateFormKeys.editorVariant]: yup
    .string()
    .required('Editor variant is required'),
  [ECampaignTemplateFormKeys.executionMode]: yup
    .string()
    .nullable()
    .oneOf([...Object.values(ECampaignExecutionMode), null])
    .when(ECampaignTemplateFormKeys.campaignType, {
      is: ECampaignType.ofuOneTime,
      then: (schema) => schema.required('Campaign execution is required'),
      otherwise: (schema) => schema.optional(),
    }),
  [ECampaignTemplateFormKeys.channels]: yup
    .array()
    .of(yup.string())
    .required('Channels are required'),
  [ECampaignTemplateFormKeys.firstOutreachTime]: yup
    .string()
    .required('First outreach time is required'),
  [ECampaignTemplateFormKeys.maxReachouts]: yup
    .number()
    .min(1, 'Max reachouts must be at least 1')
    .max(20, 'Max reachouts must be at most 20'),
  [ECampaignTemplateFormKeys.cadence]: yup
    .string()
    .when(ECampaignTemplateFormKeys.campaignType, {
      is: ECampaignType.ofu,
      then: (schema) => schema.required('Cadence is required'),
      otherwise: (schema) => schema.optional(),
    }),
  [ECampaignTemplateFormKeys.cadenceCustom]: yup
    .string()
    .when(ECampaignTemplateFormKeys.cadence, {
      is: (val: string) => val === 'custom',
      then: (schema) => schema.required('Cadence custom is required'),
      otherwise: (schema) => schema.nullable(),
    }),
  [ECampaignTemplateFormKeys.outreachInstructionsText]: yup.string().optional(),
  [ECampaignTemplateFormKeys.qualificationCriteria]: yup.string().optional(),
  [ECampaignTemplateFormKeys.verticalTags]: yup
    .array()
    .of(yup.string())
    .optional(),
  [ECampaignTemplateFormKeys.searchKeywords]: yup
    .array()
    .of(yup.string())
    .optional(),
  [ECampaignTemplateFormKeys.mergeFields]: yup
    .array()
    .of(
      yup.object({
        friendlyName: yup.string().required('Key is required'),
        variable: yup.string().required('Value is required'),
      }),
    )
    .optional(),
  [ECampaignTemplateFormKeys.introMessage]: yup.string().optional(),
  [ECampaignTemplateFormKeys.voicemailMessage]: yup.string().optional(),
  [ECampaignTemplateFormKeys.firstTextMessage]: yup.string().optional(),
  [ECampaignTemplateFormKeys.firstEmailBody]: yup.string().optional(),
  [ECampaignTemplateFormKeys.firstEmailSubject]: yup.string().optional(),
  [ECampaignTemplateFormKeys.campaignGoals]: yup.string(),
})

export type TCreateCampaignTemplatePayload = {
  [ECampaignTemplateFormKeys.friendlyName]: string
  [ECampaignTemplateFormKeys.ownerType]: string
  [ECampaignTemplateFormKeys.visibility]: string
}

export type TCreateTemplateFromCampaignForm = {
  [ECampaignTemplateFormKeys.friendlyName]: string
  [ECampaignTemplateFormKeys.ownerType]: string
  [ECampaignTemplateFormKeys.visibility]: string
  [ECampaignTemplateFormKeys.displayName]: string
  [ECampaignTemplateFormKeys.description]: string
  [ECampaignTemplateFormKeys.verticalTags]?: string[]
  [ECampaignTemplateFormKeys.searchKeywords]?: string[]
}

export const createTemplateFromCampaignSchema = yup.object({
  [ECampaignTemplateFormKeys.friendlyName]: yup
    .string()
    .min(3, 'Friendly name must be at least 3 characters')
    .max(255, 'Friendly name must be at most 255 characters')
    .required('Friendly name is required'),
  [ECampaignTemplateFormKeys.ownerType]: yup
    .string()
    .required('Owner type is required'),
  [ECampaignTemplateFormKeys.visibility]: yup
    .string()
    .required('Visibility is required'),
  [ECampaignTemplateFormKeys.displayName]: yup
    .string()
    .min(3, 'Display name must be at least 3 characters')
    .max(255, 'Display name must be at most 255 characters')
    .required('Display name is required'),
  [ECampaignTemplateFormKeys.description]: yup
    .string()
    .min(3, 'Description must be at least 3 characters')
    .max(2000, 'Description must be at most 2000 characters')
    .required('Description is required'),
  [ECampaignTemplateFormKeys.verticalTags]: yup
    .array()
    .of(yup.string())
    .required('Vertical tags are required'),
  [ECampaignTemplateFormKeys.searchKeywords]: yup
    .array()
    .of(yup.string())
    .required('Search keywords are required'),
})

export type TCreateTemplateFromCampaignInitialVersion = {
  [ECampaignTemplateFormKeys.displayName]?: string
  [ECampaignTemplateFormKeys.description]?: string
  [ECampaignTemplateFormKeys.verticalTags]?: string[]
  [ECampaignTemplateFormKeys.searchKeywords]?: string[]
}

export type TCreateTemplateFromCampaignPayload = {
  template?: Partial<TCreateCampaignTemplatePayload>
  initialVersion?: TCreateTemplateFromCampaignInitialVersion
  campaignId: string
}

export type TCreateCampaignTemplateVersionPayload = {
  [ECampaignTemplateFormKeys.campaignType]?: string
  version?: number
  status?: ETemplateVersionStatus
  [ECampaignTemplateFormKeys.editorVariant]: string
  [ECampaignTemplateFormKeys.executionMode]?: ECampaignExecutionMode | null
  [ECampaignTemplateFormKeys.displayName]: string
  [ECampaignTemplateFormKeys.description]?: string | null
  [ECampaignTemplateFormKeys.verticalTags]?: string[] | null
  [ECampaignTemplateFormKeys.searchKeywords]?: string[] | null
  campaignGoals?: string | null
  [ECampaignTemplateFormKeys.firstOutreachTime]?: string | null
  [ECampaignTemplateFormKeys.maxReachouts]?: number
  [ECampaignTemplateFormKeys.cadence]?: string
  [ECampaignTemplateFormKeys.cadenceCustom]?: string
  [ECampaignTemplateFormKeys.channels]?: ECampaignChannel[] | null
  [ECampaignTemplateFormKeys.rules]?: string[]
  [ECampaignTemplateFormKeys.outreachInstructionsText]?: string | null
  [ECampaignTemplateFormKeys.qualificationCriteria]?: string | null
  [ECampaignTemplateFormKeys.campaignFacts]?: TCampaignFact[]
  [ECampaignTemplateFormKeys.mergeFields]?: TMergeField[]
  [ECampaignTemplateFormKeys.introMessage]?: string
  [ECampaignTemplateFormKeys.voicemailMessage]?: string
  [ECampaignTemplateFormKeys.firstTextMessage]?: string
  [ECampaignTemplateFormKeys.firstEmailBody]?: string
  [ECampaignTemplateFormKeys.firstEmailSubject]?: string
}

export type TCreateCampaignWithVersionPayload = {
  template: TCreateCampaignTemplatePayload
  initialVersion: TCreateCampaignTemplateVersionPayload
}

export type TUpdateCampaignTemplatePayload = {
  template: Partial<TCreateCampaignTemplatePayload>
  initialVersion?: TCreateCampaignTemplateVersionPayload
}

export enum ECampaignContactDefaultMergeFields {
  firstName = 'first-name',
  lastName = 'last-name',
  phone = 'phone-number',
  email = 'email',
}

// Plan Metadata Types
export enum EPlanKeys {
  id = 'id',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  displayName = 'displayName',
  internalName = 'internalName',
  description = 'description',
  status = 'status',
  paymentTier = 'paymentTier',
  basePricePerLocation = 'basePricePerLocation',
  displayOrder = 'displayOrder',
  includedCalls = 'includedCalls',
  includedChats = 'includedChats',
  includedEmails = 'includedEmails',
  includedTexts = 'includedTexts',
  overageRatePerCall = 'overageRatePerCall',
  overageRatePerChat = 'overageRatePerChat',
  overageRatePerEmail = 'overageRatePerEmail',
  overageRatePerText = 'overageRatePerText',
}

export enum EPlanStatus {
  active = 'active',
  disabled = 'disabled',
}

export enum EPlanStatusFilter {
  all = 'all',
  active = 'active',
  disabled = 'disabled',
}

// Included usage of -1 means unlimited.
export const UNLIMITED_INCLUDED_USAGE = -1

export type TPlan = {
  [EPlanKeys.id]: string
  [EPlanKeys.createdDate]: Date
  [EPlanKeys.updatedDate]: Date
  [EPlanKeys.displayName]: string
  [EPlanKeys.internalName]: string
  [EPlanKeys.description]: string | null
  [EPlanKeys.status]: string
  [EPlanKeys.paymentTier]: ECognitoGroups | null
  // Integer cents, matching the OrgPlan money convention.
  [EPlanKeys.basePricePerLocation]: number | null
  [EPlanKeys.displayOrder]: number | null
  [EPlanKeys.includedCalls]: number | null
  [EPlanKeys.includedChats]: number | null
  [EPlanKeys.includedEmails]: number | null
  [EPlanKeys.includedTexts]: number | null
  // Dollar amounts per unit, not cents.
  [EPlanKeys.overageRatePerCall]: number | null
  [EPlanKeys.overageRatePerChat]: number | null
  [EPlanKeys.overageRatePerEmail]: number | null
  [EPlanKeys.overageRatePerText]: number | null
}

export type TPlanFormFields = Omit<
  TPlan,
  EPlanKeys.createdDate | EPlanKeys.updatedDate
>

// AddOn Metadata Types
export enum EAddOnKeys {
  id = 'id',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  displayName = 'displayName',
  internalName = 'internalName',
  description = 'description',
  addOnType = 'addOnType',
  stripeProductId = 'stripeProductId',
  stripePriceId = 'stripePriceId',
  status = 'status',
  unit = 'unit',
  includedUnitAmount = 'includedUnitAmount',
}

export type TAddOn = {
  [EAddOnKeys.id]: string
  [EAddOnKeys.createdDate]: Date
  [EAddOnKeys.updatedDate]: Date
  [EAddOnKeys.displayName]: string
  [EAddOnKeys.internalName]: string
  [EAddOnKeys.description]: string | null
  [EAddOnKeys.addOnType]: string
  [EAddOnKeys.stripeProductId]: string | null
  [EAddOnKeys.stripePriceId]: string | null
  [EAddOnKeys.status]: string
  [EAddOnKeys.unit]: string | null
  [EAddOnKeys.includedUnitAmount]: number | null
}

export type TAddOnFormFields = Omit<
  TAddOn,
  EAddOnKeys.createdDate | EAddOnKeys.updatedDate
>

export type TVoiceTemplateFields = {
  [EVoiceTemplateKeys.id]: string
  [EVoiceTemplateKeys.active]: boolean
  [EVoiceTemplateKeys.externalId]: string
  [EVoiceTemplateKeys.source]: string
  [EVoiceTemplateKeys.language]: string
  [EVoiceTemplateKeys.display]: EVoiceTemplateDisplays
  [EVoiceTemplateKeys.imgSource]: File | string
  [EVoiceTemplateKeys.portalDescription]: string
  [EVoiceTemplateKeys.gender]: EVoiceTemplateGender
  [EVoiceTemplateKeys.name]: string
  [EVoiceTemplateKeys.exampleRecording]: File | string
}

export type TCreateAdminVoiceTemplatePayload = {
  [EVoiceTemplateKeys.active]?: boolean
  [EVoiceTemplateKeys.externalId]: string
  [EVoiceTemplateKeys.source]?: string
  [EVoiceTemplateKeys.language]?: string | null
  [EVoiceTemplateKeys.display]: EVoiceTemplateDisplays
  [EVoiceTemplateKeys.portalDescription]: string
  [EVoiceTemplateKeys.gender]?: EVoiceTemplateGender | null
  [EVoiceTemplateKeys.name]?: string | null
  [EVoiceTemplateKeys.displayOrder]?: number | null
  [EVoiceTemplateKeys.imgSource]?: string | null
  [EVoiceTemplateKeys.exampleRecording]?: string | null
  image?: string | null
  exampleRecordingFile?: string | null
}

export type TUpdateAdminVoiceTemplatePayload =
  Partial<TCreateAdminVoiceTemplatePayload> & {
    [EVoiceTemplateKeys.id]: string
  }

export type TReorderAdminVoiceTemplateItem = {
  [EVoiceTemplateKeys.id]: string
  [EVoiceTemplateKeys.displayOrder]: number
}

export type TReorderAdminVoiceTemplatesPayload = {
  items: TReorderAdminVoiceTemplateItem[]
}

export enum EVoiceTemplatesFiltersFormKeys {
  search = 'search',
  display = 'display',
  gender = 'gender',
  status = 'status',
}

export enum EVoiceTemplatesStatusFilter {
  all = 'all',
  active = 'active',
  inactive = 'inactive',
}

export enum EVoiceTemplatesDisplayFilter {
  all = 'all',
}

export enum EVoiceTemplatesGenderFilter {
  all = 'all',
}

export type TVoiceTemplatesFiltersForm = {
  [EVoiceTemplatesFiltersFormKeys.search]: string
  [EVoiceTemplatesFiltersFormKeys.display]:
    | EVoiceTemplatesDisplayFilter
    | EVoiceTemplateDisplays
  [EVoiceTemplatesFiltersFormKeys.gender]:
    | EVoiceTemplatesGenderFilter
    | EVoiceTemplateGender
  [EVoiceTemplatesFiltersFormKeys.status]: EVoiceTemplatesStatusFilter
}

export type TGetAdminVoiceTemplatesFilters = {
  active?: boolean
  display?: EVoiceTemplateDisplays
  language?: string
  gender?: EVoiceTemplateGender
  source?: string
}

export enum ECreateNewOrgPlan {
  plus = ECognitoGroups.customerPlusPlan,
  free = ECognitoGroups.customerFreePlan,
}

export enum ECreateNewOrgFormKeys {
  userName = 'userName',
  userEmail = 'userEmail',
  businessName = 'businessName',
  organizationName = 'organizationName',
  paymentTier = 'paymentTier',
  agentLimit = 'agentLimit',
}

export type TCreateNewOrgForm = {
  [ECreateNewOrgFormKeys.userName]: string
  [ECreateNewOrgFormKeys.userEmail]: string
  [ECreateNewOrgFormKeys.businessName]: string
  [ECreateNewOrgFormKeys.organizationName]: string
  [ECreateNewOrgFormKeys.paymentTier]: ECreateNewOrgPlan
  [ECreateNewOrgFormKeys.agentLimit]: number | null
}

export type TCreateAdminOrganizationPayload = {
  userName: string
  userEmail: string
  paymentTier: ECreateNewOrgPlan
  organizationName?: string
  brand?: TBrandPayload
  partnerId?: string
  agentLimit?: number
  inviteCcEmail?: string
}

export type TCreateNewOrgSuccessResult = {
  agentId?: string
  email: string
  orgId: string
  userId: string
}

export enum EAdminOrgsPageFormKeys {
  columns = 'columns',
  filters = 'filters',
  table = 'table',
}

export enum EAdminOrgsPageTableFilterKeys {
  search = 'search',
  billingMode = 'billingMode',
  accessTier = 'accessTier',
}

export enum EAdminOrgsBillingModeFilter {
  all = '',
  paid = EOrgBillingMode.paid,
  internal = EOrgBillingMode.internal,
  inactive = EOrgBillingMode.inactive,
  demo = EOrgBillingMode.demo,
}

export enum EAdminOrgsAccessTierFilter {
  all = '',
  basic = ECognitoGroups.customerBasicPlan,
  free = ECognitoGroups.customerFreePlan,
  plus = ECognitoGroups.customerPlusPlan,
}

export enum EAdminOrgsPageTableColumnKeys {
  organization = 'organization',
  accessTier = 'accessTier',
  billingMode = 'billingMode',
  assistants = 'assistants',
  stripeId = 'stripeId',
  partner = 'partner',
  userLimit = 'userLimit',
  outboundCallLimit = 'outboundCallLimit',
  ofuContactLimit = 'ofuContactLimit',
  createdDate = 'createdDate',
}

export type TAdminOrgsPageTableFiltersValues = {
  [EAdminOrgsPageTableFilterKeys.search]: string
  [EAdminOrgsPageTableFilterKeys.billingMode]: EAdminOrgsBillingModeFilter
  [EAdminOrgsPageTableFilterKeys.accessTier]: EAdminOrgsAccessTierFilter
}

export type TAdminOrgsPageTableColumnsValues = Record<
  EAdminOrgsPageTableColumnKeys,
  boolean
>

export type TAdminOrgsPageTableParams = {
  page: number
  pageSize: number
  sortBy?: TAdminOrganizationsSortableField
  sortDirection?: EColumnSort
}

export type TAdminOrgsPageForm = {
  [EAdminOrgsPageFormKeys.columns]: TAdminOrgsPageTableColumnsValues
  [EAdminOrgsPageFormKeys.filters]: TAdminOrgsPageTableFiltersValues
  [EAdminOrgsPageFormKeys.table]: TAdminOrgsPageTableParams
}

export enum EAdminOrganizationKeys {
  id = 'id',
  name = 'name',
  paymentTier = 'paymentTier',
  billingMode = 'billingMode',
  agentLimit = 'agentLimit',
  stripeCustomerId = 'stripeCustomerId',
  partnerId = 'partnerId',
  partner = 'partner',
  userLimit = 'userLimit',
  outboundCallLimit = 'outboundCallLimit',
  ofuContactLimit = 'ofuContactLimit',
  createdDate = 'createdDate',
  activeAssistantCount = 'activeAssistantCount',
}

export type TAdminOrganizationListItem = {
  [EAdminOrganizationKeys.id]: string
  [EAdminOrganizationKeys.name]: string | null
  [EAdminOrganizationKeys.paymentTier]: string | null
  [EAdminOrganizationKeys.billingMode]: string | null
  [EAdminOrganizationKeys.agentLimit]: number | null
  [EAdminOrganizationKeys.stripeCustomerId]: string | null
  [EAdminOrganizationKeys.partnerId]: string | null
  [EAdminOrganizationKeys.partner]: {
    partnerName: string
  } | null
  [EAdminOrganizationKeys.userLimit]: number | null
  [EAdminOrganizationKeys.outboundCallLimit]: number | null
  [EAdminOrganizationKeys.ofuContactLimit]: number | null
  [EAdminOrganizationKeys.createdDate]: string
  [EAdminOrganizationKeys.activeAssistantCount]: number
}

export type TAdminOrganizationsSortableField =
  | EAdminOrganizationKeys.paymentTier
  | EAdminOrganizationKeys.billingMode
  | EAdminOrganizationKeys.name
  | EAdminOrganizationKeys.createdDate

export enum EAdminOrganizationDetailFrom {
  orgs = 'orgs',
}

export type TAdminOrganizationDetailLocationState = {
  from: EAdminOrganizationDetailFrom
}

export enum EAdminUserDetailFrom {
  users = 'users',
}

export type TAdminUserDetailLocationState = {
  from: EAdminUserDetailFrom
}

export enum EAdminOrgsAndUsersInitialTab {
  users = 'users',
}

export type TAdminOrgsAndUsersLocationState = {
  initialTab?: EAdminOrgsAndUsersInitialTab
}

export enum EAdminUsersPageFormKeys {
  columns = 'columns',
  filters = 'filters',
  table = 'table',
}

export enum EAdminUsersPageTableFilterKeys {
  search = 'search',
  status = 'status',
}

export enum EAdminUsersPageStatusFilter {
  all = '',
  active = 'active',
  invited = 'invited',
  disabled = 'disabled',
}

export enum EAdminUsersPageTableColumnKeys {
  name = 'name',
  email = 'email',
  status = 'status',
  organization = 'organization',
  userId = 'userId',
  cognitoUid = 'cognitoUid',
  createdDate = 'createdDate',
}

export enum EAdminUserKeys {
  id = 'id',
  email = 'email',
  name = 'name',
  cognitoUid = 'cognitoUid',
  orgId = 'orgId',
  status = 'status',
  createdAt = 'createdAt',
  lastLogin = 'lastLogin',
  lastSession = 'lastSession',
}

export type TAdminUsersPageTableFiltersValues = {
  [EAdminUsersPageTableFilterKeys.search]: string
  [EAdminUsersPageTableFilterKeys.status]: EAdminUsersPageStatusFilter
}

export type TAdminUsersPageTableColumnsValues = Record<
  EAdminUsersPageTableColumnKeys,
  boolean
>

export type TAdminUserListItem = {
  [EAdminUserKeys.id]: string
  [EAdminUserKeys.email]: string
  [EAdminUserKeys.name]: string | null
  [EAdminUserKeys.cognitoUid]: string | null
  [EAdminUserKeys.orgId]: string | null
  [EAdminUserKeys.status]: string | null
  [EAdminUserKeys.createdAt]: string
  [EAdminUserKeys.lastLogin]: string | null
  [EAdminUserKeys.lastSession]: string | null
  Org: {
    paymentTier: string | null
    name: string | null
  } | null
}

export type TAdminUsersSortableField =
  | EAdminUserKeys.email
  | EAdminUserKeys.id
  | EAdminUserKeys.cognitoUid
  | EAdminUserKeys.orgId
  | EAdminUserKeys.createdAt

export type TAdminUsersPageTableParams = {
  page: number
  pageSize: number
  sortBy?: TAdminUsersSortableField
  sortDirection?: EColumnSort
}

export type TAdminUsersPageForm = {
  [EAdminUsersPageFormKeys.columns]: TAdminUsersPageTableColumnsValues
  [EAdminUsersPageFormKeys.filters]: TAdminUsersPageTableFiltersValues
  [EAdminUsersPageFormKeys.table]: TAdminUsersPageTableParams
}

export type TAdminUsersPaginatedResponse = {
  data: TAdminUserListItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}
